/**
 * Canary (honeytoken) contracts shared by the engine, the guest, and
 * analysis. See docs/event-model.md §6 and ADR-0011.
 *
 * The engine generates a run's registry from the seed and context, hands it
 * to the guest (which plants the values in cookies, storage, and form
 * fields), scans sink events for the values (`AuditEvent.canaries`), and
 * sends the registry to the UI with `done`, where analysis turns hits into
 * `DataFlow`s.
 */
import { type CanaryEncoding, type CanaryHitField, isObject } from "../events";

/**
 * What kind of secret a canary stands in for. Analysis keys severity on it
 * (e.g. a `payment` or `credential` value reaching a network sink).
 */
export const CANARY_KINDS = [
  /** Passwords, auth/session tokens and cookies, URL tokens. */
  "credential",
  /** Card numbers, CVVs, expiry dates. */
  "payment",
  /** Email, name, phone, address, clipboard text. */
  "personal",
  /** Analytics and advertising ids (e.g. `_ga`). */
  "tracking",
  /** Device and browser identity (e.g. the user agent). */
  "device",
] as const;
export type CanaryKind = (typeof CANARY_KINDS)[number];

/** One honeytoken planted for a run, shaped like the real thing. */
export interface Canary {
  /** Unique within the registry, stable for a given seed and context. */
  id: string;
  /**
   * Where the value was planted, human-readable: `document.cookie:_session`,
   * `localStorage:auth_token`, `input[name=cc-number]`.
   */
  source: string;
  /** The honeytoken itself (never empty). */
  value: string;
  kind: CanaryKind;
}

/** All canaries of a run. Serializable: it travels in the `done` message. */
export interface CanaryRegistry {
  canaries: Canary[];
}

/** A registry without canaries (treat as read-only). */
export const EMPTY_REGISTRY: CanaryRegistry = { canaries: [] };

/** A canary seen leaving through a sink: `source → sink (origin)`, derived from hits. */
export interface DataFlow {
  /** The `Canary.id`. */
  canary: string;
  /** Copied from the canary, for display. */
  source: string;
  kind: CanaryKind;
  /** `seq` of the sink event the canary appeared in. */
  seq: number;
  /** The sink event's target, e.g. `navigator.sendBeacon`. */
  sink: string;
  /** The destination origin, e.g. `https://px.example`, when the sink has a URL. */
  origin?: string;
  /** Every encoding the canary was found in, in `CANARY_ENCODINGS` order. */
  encodings: CanaryEncoding[];
  /** Every field it was found in, in `CANARY_HIT_FIELDS` order. */
  fields: CanaryHitField[];
  /** True if only partial (substring) matches were found. */
  partial: boolean;
}

// ── Validators ──────────────────────────────────────────────────────────────

const isNonEmptyString = (v: unknown): v is string => typeof v === "string" && v !== "";
const isCanaryKind = (v: unknown): v is CanaryKind =>
  (CANARY_KINDS as readonly unknown[]).includes(v);

export const isCanary = (v: unknown): v is Canary =>
  isObject(v) &&
  Object.keys(v).length === 4 &&
  isNonEmptyString(v.id) &&
  typeof v.source === "string" &&
  isNonEmptyString(v.value) &&
  isCanaryKind(v.kind);

export function isCanaryRegistry(v: unknown): v is CanaryRegistry {
  if (!isObject(v) || Object.keys(v).length !== 1 || !Array.isArray(v.canaries)) return false;
  if (!v.canaries.every(isCanary)) return false;
  const ids = new Set(v.canaries.map((c: Canary) => c.id));
  return ids.size === v.canaries.length;
}
